"use client";

import type { SupSingleVaultV3 } from "@/types";
import React, { useMemo } from "react";

import UiTooltip from "@/components/Ui/Tooltip";
import UiDetailsAtomStatus from "@/components/Ui/Details/Atom/Status";
import VaultDetailsSuperPool from "@/components/Vault/Details/SuperPool";
import UiDetailsBreadcrumbs, { type DetailsBreadCrumb } from "@/components/Ui/Details/Atom/Breadcrumbs";
import PortfolioPositions from "@/components/Portfolio/Superpositions/Positions";
import UiDetailsAbout from "@/components/Ui/Details/About";
import UiDetailsAtomLinks from "@/components/Ui/Details/Atom/Links";
import UiDetailsAtomLogo from "@/components/Ui/Details/Atom/Logo";
import UiDetailsHeader from "@/components/Ui/Details/Header";
import VaultDetailsActionBar from "@/components/Vault/Details/ActionBar";
import VaultDetailsDune from "@/components/Vault/Details/Dune";
import VaultDetailsLineChart from "@/components/Vault/Details/LineChart";
import VaultDetailsStats from "@/components/Vault/Details/Stats";
import VaultDetailsTable from "@/components/Vault/Details/Table";
import useProtocolColor from "@/hooks/data/useProtocolColor";
import { hexToRGBA } from "@/utils";
import { SupAlertBar, SupHeading } from "@repo/core-ui";
import { SupAlert } from "@repo/core-ui";
import dictionaryConfig from "@/../config/dictionary.config";
import { twJoin, twMerge } from "tailwind-merge";

interface VaultPageProps {
  singleVault: SupSingleVaultV3;
}

export default function VaultPage({ singleVault }: VaultPageProps) {
  const colors = useProtocolColor(singleVault?.protocol);

  const logo = singleVault?.protocol?.graphics.icon || singleVault?.protocol?.graphics.avatar || dictionaryConfig.protocol.noProtocolImage;

  const breadCrumbs = useMemo(
    () =>
      [
        {
          href: "/vaults/",
          name: "Vaults",
        },
        Boolean(singleVault?.protocol) && {
          href: `/protocol/${!singleVault?.protocol?.vanity_url ? singleVault?.protocol?.id : singleVault?.protocol?.vanity_url}/`,
          name: singleVault?.protocol?.name ?? "",
        },
        {
          name: singleVault?.friendly_name ?? "",
        },
      ].filter(Boolean) as DetailsBreadCrumb[],
    [singleVault]
  );

  const showTooltip = singleVault?.friendly_name.length > 20;

  return (
    <section className="max-w-screen relative flex w-full grow flex-col items-stretch md:max-w-none">
      <>
        <UiDetailsHeader
          links={
            <UiDetailsAtomLinks
              links={singleVault?.protocol?.links}
              shareLink={`protocol/${singleVault?.protocol?.vanity_url ? singleVault?.protocol?.vanity_url : singleVault?.protocol?.id}`}
            />
          }
          banner={singleVault?.protocol?.is_authenticated ? singleVault?.protocol?.graphics.header : undefined}
        >
          {singleVault?.protocol?.is_authenticated && (
            <UiDetailsAtomLogo logo={logo} title={singleVault?.protocol?.name} className="relative -translate-y-2 md:absolute md:left-6 md:top-0 md:-translate-y-1/2" />
          )}
        </UiDetailsHeader>

        <div className="grid grid-cols-2">
          <div className="col-span-2 flex flex-1 flex-col items-center px-4 md:order-2 md:flex-col-reverse md:items-start md:px-6 xl:col-span-1">
            <UiDetailsBreadcrumbs
              className={twJoin("no-scrollbar max-w-full overflow-y-auto", !singleVault?.protocol?.is_authenticated ? "mb-2 mt-4" : "")}
              breadCrumbs={breadCrumbs}
            />

            <div className="mt-5 flex max-w-full flex-1 flex-col items-center overflow-hidden md:mt-2 md:flex-col-reverse md:items-start">
              <div className="inline-flex items-center md:mb-4 xl:mb-9">
                <UiTooltip wrapper content={singleVault?.friendly_name} enable={showTooltip} arrowPlacement="bottom-start">
                  <SupHeading
                    variant="h2"
                    family="sans"
                    weight="black"
                    className={twMerge(
                      "max-w-full text-center text-3xl text-neutral-925 md:overflow-hidden md:text-ellipsis md:whitespace-nowrap md:text-left md:text-5xl md:leading-none xl:text-7xl",
                      showTooltip && "cursor-help select-none"
                    )}
                  >
                    {singleVault?.friendly_name}
                  </SupHeading>
                </UiTooltip>
              </div>

              <div className="mb-1 flex flex-col items-center gap-2 md:flex-row">
                <SupHeading variant="h6" family="sans" weight="bold" className="leading-none">
                  {singleVault?.protocol?.name}
                </SupHeading>
                <UiDetailsAtomStatus auth={singleVault?.protocol?.is_authenticated} logo={logo} />
                <VaultDetailsSuperPool singleVault={singleVault} />
              </div>
            </div>
          </div>

          <div className="col-span-2 my-6 flex justify-center md:order-1 md:mt-2 md:justify-end xl:mb-2 xl:mt-6">
            <VaultDetailsActionBar singleVault={singleVault} />
          </div>

          <div className="col-span-2 flex px-4 md:order-3 md:mt-8 md:px-6 xl:col-span-1 xl:mt-0 xl:items-end">
            <VaultDetailsStats singleVault={singleVault} />
          </div>
        </div>

        <SupAlertBar
          closeable
          visible={Boolean(singleVault.vault_status_message)}
          icon={<SupAlert />}
          title="Vault status"
          content={singleVault.vault_status_message}
          className="mx-6 my-4"
          style={colors.isCustomColor ? { backgroundColor: hexToRGBA(colors.color, 0.08), color: colors.color } : undefined}
        />

        <PortfolioPositions className="mx-4 md:mx-6" singleVault={singleVault} />

        <UiDetailsAbout
          aboutText={singleVault.description}
          chart={<VaultDetailsLineChart singleVault={singleVault} />}
          table={<VaultDetailsTable vault={singleVault} />}
          colors={colors}
        />

        {singleVault.analytics.length > 0 && <VaultDetailsDune className="mx-4 md:mx-6" slides={singleVault.analytics} />}
      </>
    </section>
  );
}
